import { REACT_APP_FRONTEND_HOSTNAME } from 'env'
import { History } from 'history'
import linkifyHtml from 'linkify-html'
import { last } from 'lodash'
import qs from 'qs'
import { generatePath, matchPath } from 'react-router-dom'

import {
  ALL_FILES_PATH,
  ARTICLE_REVIEW_PATH,
  ARTICLE_SUBMIT_REVIEW_PATH,
  DESIGN_PATH,
  VIDEO_PATH,
  DOC_PATH,
  FOLDER_PATH,
  FOLDER_VIEWS_PATH,
  GROUPS_PATH,
  GROUP_PATH,
  IMPORT_STATE_PATH,
  LOGIN_OR_SIGNUP_PATH,
  LOGIN_PATH,
  RECENT_DOCS_PATH,
  SIGNUP_PATH,
  WORKSPACE_SELECTION_PATH,
} from 'paths'
import { Auth, Opaque } from 'types'
import { HOSTNAMES } from './constants'
import { frontendHostname, frontendOrigin } from './frontendOrigin'

const UNSAFE_CHARS = /[/!*();:@&=+$/?%#[\]"<>{}|\\^~` ]/g

export const memberPath = (member: { id: number; seo_string?: string }) => `/users/${member.id}/${member.seo_string}`

/**
 * Parses the params from a URL string and returns them in an object
 *
 * @param {string} url URL with param(s) or just the params query string
 * @return {Object} Object of param key-value pairs
 */
export const getUrlParams: (url: string) => Record<string, string> = (url: string): {} =>
  qs.parse(url.split('?').slice(1).join(''))

export const redirectToLoginOrSignup = (
  history: History,
  { whenDoneRedirectTo = null, path = LOGIN_OR_SIGNUP_PATH }: { whenDoneRedirectTo?: string | null; path?: string },
) => {
  const { location } = history
  const redirectAfterLogin = whenDoneRedirectTo || `${location.pathname}${location.search}`
  history.push({ pathname: path, search: `?redirect_to=${redirectAfterLogin}` })
}

export const redirectToLogin = (
  history: History,
  { whenDoneRedirectTo }: { whenDoneRedirectTo?: string | null } = { whenDoneRedirectTo: null },
) => {
  redirectToLoginOrSignup(history, { whenDoneRedirectTo, path: LOGIN_PATH })
}

export const redirectToSignup = (
  history: History,
  { whenDoneRedirectTo }: { whenDoneRedirectTo?: string | null } = { whenDoneRedirectTo: null },
) => {
  redirectToLoginOrSignup(history, { whenDoneRedirectTo, path: SIGNUP_PATH })
}

export const getArticleKeyFromUrl = (url: string | undefined): string | undefined => {
  if (!url) return

  const slug = last(url.split('/'))
  if (!slug) return

  return last(slug.split('-'))
}

export const pathAfterLogin = (history: History, auth: Auth) => {
  const { location } = history
  if (auth.redirect_to) {
    return auth.redirect_to
  }
  if (
    location.state &&
    location.state.next &&
    !location.state.next.includes('login') &&
    !location.state.next.includes('verify')
  ) {
    return `${location.state.next}${location.search}`
  } else {
    return WORKSPACE_SELECTION_PATH
  }
}

export const getArticleIdentifier = (pathname: string): string | undefined => {
  const isDocView = matchPath(pathname, { path: DOC_PATH })
  if (!!isDocView) return getArticleKeyFromUrl(pathname)

  return pathname.slice(1)
}

// @TODO 171982480
export const redirectAfterLogin = (history: History, auth: Auth) => {
  const path = pathAfterLogin(history, auth)

  history.push(path)
}

/**
 * Returns the almanac url for a given pathname
 *
 * @param {string} pathname
 * @returns {string} the almanac url
 */
export const getAlmanacUrlFor = (pathname: string): string =>
  `${frontendOrigin()}${pathname.startsWith('/') ? '' : '/'}${pathname}`

/**
 * Strips http, https, www, and trailing slash from a url
 *
 * @param {string} url
 * @returns {string} the display url, for example 'almanac.io' instead of 'https://www.almanac.io/'
 */
export const getDisplayUrl = (url: string): string =>
  url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/+$/, '')

/**
 * Takes a url and returns the pathname
 *
 * @param {string} url
 * @returns {string} pathname
 */
export const getPathname = (url: string): string => {
  if (url.startsWith('http')) {
    let parser = document.createElement('a')
    parser.href = url
    url = parser.pathname
  }

  return url
}

export const parseUrlsInString = (text: string): string => {
  return linkifyHtml(text, {
    attributes: {
      target: '_blank',
    },
  })
}

/**
 * Takes a user object and returns the path to the user's profile
 *
 * @param {object} user
 * @returns {string} path or empty string if the id was not set
 */
export const getUserProfilePath = (user: { displayName?: string | null; id?: number | null }): string => {
  if (!user.id) {
    return ''
  }

  return `/users/${user.id}/${(user.displayName || 'No name')
    .toLowerCase()
    .replace(',', '')
    .replace(UNSAFE_CHARS, '-')}`
}

/**
 * Takes a user object and returns the path to the user's workspace profile
 *
 * @param {object} user
 * @returns {string} path or empty string if the id or workspace ID was not set
 */
export const getUserWorkspaceProfilePath = (user: { workspaceId: number | null; id: number | null }): string => {
  if (!user.id || !user.workspaceId) {
    return ''
  }

  return `/workspaces/${user.workspaceId}/members?userId=${user.id}`
}

/**
 * Is the URL a url for an image?
 *
 * @param {?string} url
 * @returns {boolean} whether or not url is an image url
 */
export const isImageUrl = (url?: string): boolean => {
  return !!url && getPathname(url).match(/\.(jpeg|jpg|gif|png|webp)$/) !== null
}

export const getArticlePath = ({ slug }: { slug: string }) => {
  return generatePath(DOC_PATH, { slug: slug })
}

export const getDesignPath = ({ slug }: { slug: string }) => {
  return generatePath(DESIGN_PATH, { key: slug })
}

export const getVideoPath = ({ slug }: { slug: string }) => {
  return generatePath(VIDEO_PATH, { key: slug })
}

export const getInformalFeedbackRequestSubmitPath = ({
  articleKey,
  reviewId,
}: {
  articleKey: string
  reviewId: string
}) => {
  return generatePath(ARTICLE_SUBMIT_REVIEW_PATH, { slug: articleKey, reviewId })
}

export const getInformalFeedbackRequestPath = ({ articleKey, reviewId }: { articleKey: string; reviewId: string }) => {
  return generatePath(ARTICLE_REVIEW_PATH, { slug: articleKey, reviewId })
}

export const getArticleUrl = (key: string) => {
  return `${frontendOrigin()}${getArticlePath({ slug: key })}`
}

export const getLayerUrl = (articleKey: string, id: string) => {
  return `${getArticleUrl(articleKey)}?layer=${id}`
}

export const getFolderUrl = (folderSlug: string) => {
  return `${frontendOrigin()}${getFolderPath({ folderSlug })}`
}

export const getImportPath = ({ workspaceId, key }: { workspaceId: number; key: string }) => {
  return generatePath(IMPORT_STATE_PATH, { workspaceId, key })
}

export const getFolderPath = ({ folderSlug }: { folderSlug: string }) => {
  return generatePath(FOLDER_PATH, { folderSlug })
}

export const getFolderViewPath = ({ folderSlug, view }: { folderSlug: string; view: string | number }) => {
  return generatePath(FOLDER_VIEWS_PATH, { folderSlug, view })
}

export const getProjectUrl = ({ workspaceId, id }: { workspaceId: number; id: string }) => {
  return `${frontendOrigin()}${generatePath(ALL_FILES_PATH, { workspaceId, location: id })}`
}

export const getAllDocsUrl = (workspaceId: number) => {
  return `${frontendOrigin()}${getAllDocsPath(workspaceId)}`
}

export const getAllDocsPath = (workspaceId: number) => {
  return generatePath(ALL_FILES_PATH, { workspaceId })
}

export const getRecentDocsPath = (workspaceId: number) => {
  return generatePath(RECENT_DOCS_PATH, { workspaceId })
}

export const getGroupsUrl = (workspaceId: number) => {
  return `${frontendOrigin()}${generatePath(GROUPS_PATH, { workspaceId })}`
}

export const getGroupPath = ({ workspaceId, groupId }: { workspaceId: number; groupId: number }) => {
  return generatePath(GROUP_PATH, { workspaceId, id: groupId })
}

export const getSettingsMemberUrl = (workspaceId: number) => {
  return `${frontendOrigin()}/workspaces/${workspaceId}?settings=members`
}

export type InternalUrl = Opaque<string, 'InternalUrl'>
export const isAlmanacUrl = (href: string): href is InternalUrl => {
  if (href.startsWith('#')) {
    return true
  }

  let url
  try {
    url = new URL(href)
  } catch {
    return false
  }

  return HOSTNAMES.includes(url.hostname)
}

export const isAlmanacOrCustomDomainUrl = (href: string): href is InternalUrl => {
  let url
  try {
    url = new URL(href)
  } catch {
    return false
  }

  const domain = frontendHostname()
  return url.host.endsWith(domain) || url.host.endsWith(REACT_APP_FRONTEND_HOSTNAME)
}

export const isFolderDocPath = (href: string) => {
  const folderRegex = /\/folders\/.+\/.+/
  return href.match(folderRegex)
}

export const isDocPath = (href: string) => {
  const docRegex = /\/docs\//
  return href.match(docRegex)
}

export const getSearchString = (params: { [key: string]: string }): string => new URLSearchParams(params).toString()

export const currentPath = (location: any) => encodeURIComponent(`${location.pathname}${location.search}`)

export const isAlmanacDocUrl = (href: string) => {
  return isAlmanacOrCustomDomainUrl(href) && (isDocPath(href) || isFolderDocPath(href))
}
